import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Slider } from '../../components/ui/slider';
import { Button } from '../../components/ui/button';

const formatINR = (n) => {
  if (!isFinite(n)) return '₹0';
  return '₹' + Math.round(n).toLocaleString('en-IN');
};

const EMICalculator = () => {
  const [amount, setAmount] = useState(1000000);
  const [rate, setRate] = useState(9.5);
  const [tenure, setTenure] = useState(10);

  const { emi, totalInterest, totalPayment } = useMemo(() => {
    const P = amount;
    const r = rate / 12 / 100;
    const n = tenure * 12;
    if (P <= 0 || r <= 0 || n <= 0) return { emi: 0, totalInterest: 0, totalPayment: 0 };
    const e = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return { emi: e, totalInterest: e * n - P, totalPayment: e * n };
  }, [amount, rate, tenure]);

  const interestPct = totalPayment > 0 ? (totalInterest / totalPayment) * 100 : 0;

  return (
    <CalculatorLayout
      title="EMI Calculator"
      subtitle="Calculate Equated Monthly Installment for home, car, and personal loans in Indian Rupees."
      category="Financial"
      description={<>
        <p>An EMI (Equated Monthly Installment) is a fixed payment made by a borrower to a lender every month. It consists of both principal and interest components.</p>
        <p>Use this calculator to plan your loan repayments in <strong>INR (₹)</strong> and understand the total interest cost over the tenure.</p>
      </>}
      formulaNote="EMI = P × r × (1 + r)ⁿ / ((1 + r)ⁿ − 1)    where P = principal, r = monthly rate, n = months"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Inputs */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-slate-700">Loan Amount</Label>
              <div className="flex items-center gap-1 bg-saffron-50 rounded-md px-2 py-1">
                <span className="text-saffron-700 font-semibold text-sm">₹</span>
                <Input type="number" value={amount} onChange={(e) => setAmount(Math.max(0, +e.target.value))} className="w-28 h-7 border-0 bg-transparent p-0 text-right font-semibold text-saffron-700 focus-visible:ring-0" />
              </div>
            </div>
            <Slider min={50000} max={20000000} step={50000} value={[amount]} onValueChange={(v) => setAmount(v[0])} />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>₹50K</span><span>₹2Cr</span></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-slate-700">Interest Rate (p.a.)</Label>
              <div className="flex items-center gap-1 bg-indgreen-50 rounded-md px-2 py-1">
                <Input type="number" step="0.1" value={rate} onChange={(e) => setRate(Math.max(0, +e.target.value))} className="w-16 h-7 border-0 bg-transparent p-0 text-right font-semibold text-indgreen-700 focus-visible:ring-0" />
                <span className="text-indgreen-700 font-semibold text-sm">%</span>
              </div>
            </div>
            <Slider min={1} max={25} step={0.1} value={[rate]} onValueChange={(v) => setRate(v[0])} />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1%</span><span>25%</span></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium text-slate-700">Tenure</Label>
              <div className="flex items-center gap-1 bg-slate-100 rounded-md px-2 py-1">
                <Input type="number" value={tenure} onChange={(e) => setTenure(Math.max(1, +e.target.value))} className="w-14 h-7 border-0 bg-transparent p-0 text-right font-semibold focus-visible:ring-0" />
                <span className="text-slate-700 font-semibold text-sm">yr</span>
              </div>
            </div>
            <Slider min={1} max={30} step={1} value={[tenure]} onValueChange={(v) => setTenure(v[0])} />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1"><span>1 yr</span><span>30 yr</span></div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2">
            {[{ n: 'Home Loan', a: 5000000, r: 8.5, t: 20 }, { n: 'Car Loan', a: 800000, r: 9.5, t: 5 }, { n: 'Personal', a: 300000, r: 12, t: 3 }].map(p => (
              <Button key={p.n} size="sm" variant="outline" onClick={() => { setAmount(p.a); setRate(p.r); setTenure(p.t); }} className="text-xs border-slate-300">
                {p.n}
              </Button>
            ))}
          </div>
        </div>

        {/* Results */}
        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Monthly EMI</div>
          <div className="font-display text-3xl md:text-4xl font-bold text-bharat-navy display-font">{formatINR(emi)}</div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-white rounded-lg p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Principal</div>
              <div className="font-semibold text-bharat-navy mt-1 display-font text-sm">{formatINR(amount)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Interest</div>
              <div className="font-semibold text-saffron-700 mt-1 display-font text-sm">{formatINR(totalInterest)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-slate-100 col-span-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">Total Payment (Principal + Interest)</div>
              <div className="font-semibold text-indgreen-700 mt-1 display-font text-lg">{formatINR(totalPayment)}</div>
            </div>
          </div>

          {/* Visual breakdown */}
          <div className="mt-5">
            <div className="text-xs text-slate-500 mb-2 flex justify-between">
              <span>Principal <span className="text-bharat-navy font-semibold">{(100 - interestPct).toFixed(1)}%</span></span>
              <span>Interest <span className="text-saffron-700 font-semibold">{interestPct.toFixed(1)}%</span></span>
            </div>
            <div className="h-3 rounded-full bg-slate-200 overflow-hidden flex">
              <div style={{ width: `${100 - interestPct}%` }} className="bg-indgreen-500" />
              <div style={{ width: `${interestPct}%` }} className="bg-saffron-500" />
            </div>
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

export default EMICalculator;
